import { isInt } from 'utils/validation';
import { GLYPHS } from 'components/Shared/Icon';

const isGoogleDefined = () => typeof window.google !== 'undefined' && window.google !== null;

export const MARKER_TYPE = {
    DEFAULT: 'default',
    STYLED: 'styled',
    CUSTOM: 'custom',
};

export const STYLED_MARKER = [GLYPHS.MARKER1, GLYPHS.MARKER2, GLYPHS.MARKER3, GLYPHS.MARKER4, GLYPHS.MARKER5];

export const getIconOptions = (icon) => {
    if (!icon || !icon.type) return undefined;
    switch (icon.type) {
        case MARKER_TYPE.STYLED:
            return getStyledIconOptions(icon);

        case MARKER_TYPE.CUSTOM:
            return getCustomIconOptions(icon);

        default:
            return undefined;
    }
};

export const isIconOptimized = (icon) => {
    if (icon && icon.type === MARKER_TYPE.CUSTOM && icon.image && icon.image.url && /\.gif$/.test(icon.image.url)) {
        return false;
    }
    return true;
};

//
// Styled Markers
//

const STYLED_MARKER_SIZE = 22;

export const getStyledIconOptions = (icon) => {
    const { styledMarkerId, color, size } = icon;
    const styledMarker = STYLED_MARKER[styledMarkerId];
    if (!styledMarker) {
        return undefined;
    }

    const path = MARKER_PATHS[styledMarker];
    if (!path) {
        return undefined;
    }

    // Use the options defined by Google Maps Symbols:
    // https://developers.google.com/maps/documentation/javascript/symbols
    return {
        path: path,
        scale: getScaleFromSize(size),
        anchor: {
            x: STYLED_MARKER_SIZE / 2,
            y: STYLED_MARKER_SIZE,
        },
        fillOpacity: 1,
        fillColor: color,
        strokeOpacity: 0,
    };
};

// Google doesn't take a width and height for this type of icon.
// Determine how much to scale the icon by compared to the base image size.
const getScaleFromSize = (size) => {
    if (!isInt(size)) {
        size = STYLED_MARKER_SIZE;
    }
    return parseFloat((size / STYLED_MARKER_SIZE).toFixed(4));
};

const MARKER_PATHS = {
    [GLYPHS.MARKER1]:
        'M11 2c-3.9 0-7 3.1-7 7 0 5.3 7 13 7 13 0 0 7-7.7 7-13 0-3.9-3.1-7-7-7Zm0 9.5c-1.4 0-2.5-1.1-2.5-2.5 0-1.4 1.1-2.5 2.5-2.5 1.4 0 2.5 1.1 2.5 2.5 0 1.4-1.1 2.5-2.5 2.5Z',
    [GLYPHS.MARKER2]:
        'M17.3 2l-12.7 0c-1 0-1.8 0.8-1.8 1.8l0 11.8c0 0.6 0.3 1.2 0.8 1.5l7.4 4.9 7.4-4.9c0.5-0.3 0.8-0.9 0.8-1.5l0-11.8c0-1-0.8-1.8-1.8-1.8l-0.1 0Z',
    [GLYPHS.MARKER3]:
        'M18 2c1.1 0 2 0.9 2 2l0 13c0 1.1-0.9 2-2 2l-4 0 -3 3 -3-3 -4 0c-1.1 0-2-0.9-2-2l0-13c0-1.1 0.9-2 2-2l14 0Zm-7 10.9l2.8 1.7 -0.7-3.2 2.5-2.1 -3.2-0.3 -1.3-3 -1.3 3 -3.2 0.3 2.5 2.1 -0.7 3.2 2.8-1.7Z',
    [GLYPHS.MARKER4]:
        'M10 13.9c-2.8-0.5-5-2.9-5-5.9 0-3.3 2.7-6 6-6 3.3 0 6 2.7 6 6 0 3-2.2 5.4-5 5.9l0 8.1 -2 0 0-8.1',
    [GLYPHS.MARKER5]: 'M20 5.9l-10-4.9 0 21 2 0 0-12 8-4.1Z',
};

//
// Custom Markers
//

// Use the options defined by Google Maps Icons
// https://developers.google.com/maps/documentation/javascript/3.exp/reference#Icon
const getCustomIconOptions = (icon) => {
    const { anchorX, anchorY, image } = icon;
    let { width, height } = icon;

    if (!image || !image.url || !isGoogleDefined()) {
        return undefined;
    }

    width = isInt(width) ? width : 36;
    height = isInt(height) ? height : 36;

    const opts = { url: image.url };
    if (isInt(width) && isInt(height)) {
        opts.scaledSize = new window.google.maps.Size(width, height);
        opts.size = new window.google.maps.Size(width, height);
    }

    if (isInt(anchorX) && isInt(anchorY)) {
        opts.anchor = new window.google.maps.Point(anchorX, anchorY);
    }

    return opts;
};
